import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError, createIntegratedPlan, errorDetails, errorMessage, getHandoff } from "../api";
import { Banner, ErrorDetails, IndeterminateProgress, LoadingLine } from "../components/Feedback";
import { SelectedPlanGroups } from "../components/SelectedPlanGroups";
import { resolveRegionalDestination, type PlannerRegion } from "../regional-destination";
import type { HandoffSummary, IntegratedPlanResult, PlanMetric } from "../types";

const HANDOFF_POLL_INTERVAL_MS = 750;
const HANDOFF_WAIT_TIMEOUT_MS = 30_000;
const DEFAULT_MAX_ITEMS_PER_GROUP = "200";
const LOCAL_DEVELOPMENT_REGION: PlannerRegion | undefined = import.meta.env.DEV ? "my" : undefined;

type ScreenState =
  | { readonly kind: "direct" }
  | { readonly kind: "waiting"; readonly handoffId: string }
  | { readonly kind: "ready"; readonly handoff: HandoffSummary }
  | { readonly kind: "planning"; readonly handoff: HandoffSummary }
  | {
      readonly kind: "success";
      readonly handoff: HandoffSummary;
      readonly plan: IntegratedPlanResult;
    }
  | { readonly kind: "expired" }
  | { readonly kind: "unauthorized" }
  | { readonly kind: "error"; readonly error: unknown };

export function PlanningPage() {
  const handoffId = useMemo(() => readHandoffId(), []);
  const regionalDestination = useMemo(
    () => resolveRegionalDestination(window.location.origin, LOCAL_DEVELOPMENT_REGION),
    [],
  );
  const boostwareUrl = regionalDestination?.dashboardUrl ?? null;
  const [screen, setScreen] = useState<ScreenState>(
    handoffId === null ? { kind: "direct" } : { kind: "waiting", handoffId },
  );
  const [maxSku, setMaxSku] = useState(32);
  const [maxOrders, setMaxOrders] = useState(50);
  const [maxItems, setMaxItems] = useState(DEFAULT_MAX_ITEMS_PER_GROUP);
  const [planError, setPlanError] = useState<unknown>(null);
  const activePlanRef = useRef<AbortController | null>(null);
  const planGenerationRef = useRef(0);
  const resultHeadingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (handoffId === null) return undefined;
    const controller = new AbortController();
    let active = true;
    setPlanError(null);
    setScreen({ kind: "waiting", handoffId });

    void waitForHandoff(handoffId, controller.signal)
      .then((handoff) => {
        if (!active) return;
        setMaxSku(handoff.limits.maxSkuPerGroup.default);
        setMaxOrders(handoff.limits.maxOrdersPerGroup.default);
        setMaxItems(DEFAULT_MAX_ITEMS_PER_GROUP);
        setScreen({ kind: "ready", handoff });
      })
      .catch((reason: unknown) => {
        if (!active || isAbortError(reason)) return;
        setScreen(classifyFatalState(reason));
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [handoffId]);

  useEffect(() => {
    if (screen.kind === "success") resultHeadingRef.current?.focus();
  }, [screen]);

  useEffect(
    () => () => {
      planGenerationRef.current += 1;
      activePlanRef.current?.abort();
      activePlanRef.current = null;
    },
    [],
  );

  const handoff = handoffFromState(screen);
  const busy = screen.kind === "planning";
  const itemsValid =
    maxItems === "" || (Number.isSafeInteger(Number(maxItems)) && Number(maxItems) >= maxOrders);
  const formValid =
    handoff !== null &&
    Number.isInteger(maxSku) &&
    maxSku >= handoff.limits.maxSkuPerGroup.minimum &&
    maxSku <= handoff.limits.maxSkuPerGroup.maximum &&
    Number.isInteger(maxOrders) &&
    maxOrders >= handoff.limits.maxOrdersPerGroup.minimum &&
    maxOrders <= handoff.limits.maxOrdersPerGroup.maximum &&
    itemsValid;

  async function generate(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const currentHandoff = handoffFromState(screen);
    if (!formValid || currentHandoff === null || busy || activePlanRef.current !== null) return;
    const controller = new AbortController();
    const generation = planGenerationRef.current + 1;
    planGenerationRef.current = generation;
    activePlanRef.current = controller;
    const isCurrent = (): boolean =>
      planGenerationRef.current === generation &&
      activePlanRef.current === controller &&
      !controller.signal.aborted;

    setPlanError(null);
    setScreen({ kind: "planning", handoff: currentHandoff });
    try {
      const plan = await createIntegratedPlan(
        currentHandoff.handoffId,
        maxSku,
        maxOrders,
        controller.signal,
        maxItems === "" ? undefined : Number(maxItems),
      );
      if (!isCurrent()) return;
      if (plan.handoffId !== currentHandoff.handoffId) {
        throw new ApiError(
          "The Planner returned a plan for a different handoff.",
          502,
          "INVALID_API_RESPONSE",
        );
      }
      setScreen({ kind: "success", handoff: currentHandoff, plan });
    } catch (reason) {
      if (!isCurrent() || isAbortError(reason)) return;
      const fatal = fatalPlanState(reason);
      if (fatal !== null) setScreen(fatal);
      else {
        setPlanError(reason);
        setScreen({ kind: "ready", handoff: currentHandoff });
      }
    } finally {
      if (planGenerationRef.current === generation && activePlanRef.current === controller) {
        activePlanRef.current = null;
      }
    }
  }

  return (
    <div className="page page-planning">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Operations workspace</span>
          <h1>Create pick plan</h1>
          <p>
            Receive filtered orders from BoostWare, set the group limits, then create Bulk Pick jobs
            from the selected plan.
          </p>
        </div>
        {handoff !== null && (
          <div className="handoff-chip" role="status">
            <span className="status-dot" aria-hidden="true" />
            <span>
              <small>Orders received</small>
              <strong>{formatNumber(handoff.orderCount)} ready to plan</strong>
            </span>
          </div>
        )}
      </div>

      {screen.kind === "direct" && <DirectState boostwareUrl={boostwareUrl} />}
      {screen.kind === "waiting" && <WaitingState />}
      {screen.kind === "expired" && <ExpiredState boostwareUrl={boostwareUrl} />}
      {screen.kind === "unauthorized" && <UnauthorizedState boostwareUrl={boostwareUrl} />}
      {screen.kind === "error" && (
        <LoadErrorState error={screen.error} boostwareUrl={boostwareUrl} />
      )}

      {handoff !== null && (
        <div className="planning-grid">
          <section className="panel plan-form-panel" aria-labelledby="plan-form-title">
            <div className="panel-heading">
              <span className="step-number">1</span>
              <div>
                <h2 id="plan-form-title">Orders and group limits</h2>
                <p>Orders and location priorities were received securely from BoostWare.</p>
              </div>
            </div>

            <dl className="handoff-summary" aria-label="Handoff summary">
              <div>
                <dt>Orders</dt>
                <dd>{formatNumber(handoff.orderCount)}</dd>
              </div>
              <div>
                <dt>Unique SKU</dt>
                <dd>{formatNumber(handoff.uniqueSkuCount)}</dd>
              </div>
              <div>
                <dt>Locations</dt>
                <dd>{formatNumber(handoff.uniqueLocationCount)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd className="ready-status">Ready</dd>
              </div>
            </dl>

            <Banner
              tone={handoff.largeHandoff ? "warning" : "info"}
              title={handoff.largeHandoff ? "Large planning handoff" : "Planning quality guidance"}
            >
              For the best planning quality, use 2,000 orders or fewer per run. Larger handoffs are
              supported up to 5,000, but may receive less optimization time.
            </Banner>

            <form onSubmit={(event) => void generate(event)}>
              <div className="form-grid">
                <label className="field">
                  <span>Max Unique SKU / Group</span>
                  <input
                    type="number"
                    min={handoff.limits.maxSkuPerGroup.minimum}
                    max={handoff.limits.maxSkuPerGroup.maximum}
                    step={1}
                    value={maxSku}
                    disabled={busy}
                    aria-describedby="max-sku-help"
                    onChange={(event) => setMaxSku(event.currentTarget.valueAsNumber)}
                  />
                  <small id="max-sku-help">
                    {formatNumber(handoff.limits.maxSkuPerGroup.minimum)} to{" "}
                    {formatNumber(handoff.limits.maxSkuPerGroup.maximum)}
                  </small>
                </label>
                <label className="field">
                  <span>Max Orders / Group</span>
                  <input
                    type="number"
                    min={handoff.limits.maxOrdersPerGroup.minimum}
                    max={handoff.limits.maxOrdersPerGroup.maximum}
                    step={1}
                    value={maxOrders}
                    disabled={busy}
                    aria-describedby="max-orders-help"
                    onChange={(event) => setMaxOrders(event.currentTarget.valueAsNumber)}
                  />
                  <small id="max-orders-help">
                    {formatNumber(handoff.limits.maxOrdersPerGroup.minimum)} to{" "}
                    {formatNumber(handoff.limits.maxOrdersPerGroup.maximum)}
                  </small>
                </label>
                <label className="field">
                  <span id="max-items-label">Max Items / Group</span>
                  <input
                    type="number"
                    min={Number.isInteger(maxOrders) ? maxOrders : 1}
                    max={Number.MAX_SAFE_INTEGER}
                    step={1}
                    value={maxItems}
                    disabled={busy}
                    placeholder="No item limit"
                    aria-labelledby="max-items-label"
                    aria-describedby={
                      itemsValid ? "max-items-help" : "max-items-help max-items-error"
                    }
                    aria-invalid={!itemsValid}
                    onChange={(event) => setMaxItems(event.currentTarget.value)}
                  />
                  <small id="max-items-help">
                    Total units across all SKUs and orders. Leave empty for no item limit.
                    Otherwise, enter a whole number at least equal to Max Orders / Group.
                  </small>
                  {!itemsValid && (
                    <small id="max-items-error" role="alert">
                      Max Items / Group must be a whole number greater than or equal to Max Orders /
                      Group.
                    </small>
                  )}
                </label>
              </div>
              {planError !== null && (
                <Banner tone="error" title={errorMessage(planError)}>
                  <ErrorDetails items={errorDetails(planError)} />
                </Banner>
              )}
              {busy && <IndeterminateProgress label="Creating your pick plan…" />}
              <button
                className="button button-primary button-large"
                type="submit"
                disabled={!formValid || busy}
              >
                {busy ? "Creating plan…" : "Create pick plan"}
              </button>
            </form>
          </section>

          <aside className="panel context-panel" aria-labelledby="context-title">
            <div className="panel-heading compact">
              <span className="step-number muted">2</span>
              <h2 id="context-title">Before you run</h2>
            </div>
            <ul className="check-list">
              <li>Each order already includes its allocated location priorities.</li>
              <li>A Bulk Pick group can contain up to 100 orders.</li>
              <li>Keep this tab open while the Worker creates the selected plan.</li>
            </ul>
            <dl className="detail-list">
              <div>
                <dt>Received</dt>
                <dd>{formatDate(handoff.createdAt)}</dd>
              </div>
              <div>
                <dt>Expires</dt>
                <dd>{formatDate(handoff.expiresAt)}</dd>
              </div>
              <div>
                <dt>Handoff</dt>
                <dd className="mono">{shortId(handoff.handoffId)}</dd>
              </div>
            </dl>
          </aside>
        </div>
      )}

      {screen.kind === "success" && (
        <section className="panel result-panel" aria-labelledby="result-title">
          <div className="result-heading">
            <div>
              <span className="success-icon" aria-hidden="true">
                ✓
              </span>
              <div>
                <h2 id="result-title" ref={resultHeadingRef} tabIndex={-1}>
                  Pick plan ready
                </h2>
                <p>
                  Selected plan · {formatNumber(screen.plan.metrics.groupCount)}{" "}
                  {screen.plan.metrics.groupCount === 1 ? "group" : "groups"}
                </p>
              </div>
            </div>
          </div>
          <Banner tone="info" title="Why this plan was selected">
            {screen.plan.selection.explanation}
          </Banner>
          <div className="metric-grid">
            {planMetrics(screen.plan).map((metric) => (
              <div
                className={`metric-card${metric.emphasized === true ? " emphasized" : ""}`}
                key={metric.label}
              >
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
              </div>
            ))}
          </div>
          <SelectedPlanGroups
            key={screen.plan.generatedAt}
            groups={screen.plan.groups}
            bulkPickBaseUrl={screen.plan.bulkPickBaseUrl}
            plannerOrigin={window.location.origin}
            localDevelopmentRegion={LOCAL_DEVELOPMENT_REGION}
          />
        </section>
      )}
    </div>
  );
}

function DirectState({ boostwareUrl }: { boostwareUrl: string | null }) {
  return (
    <section className="panel workflow-state-panel" aria-labelledby="direct-title">
      <span className="state-icon" aria-hidden="true">
        ↗
      </span>
      <div>
        <h2 id="direct-title">Open Pick Planner from BoostWare to begin</h2>
        <p>Use Planning on the BoostWare Pick &amp; Pack page to send orders here.</p>
      </div>
      <BoostWareLink href={boostwareUrl} label="Open BoostWare" />
    </section>
  );
}

function WaitingState() {
  return (
    <section className="panel workflow-state-panel waiting-state" aria-labelledby="waiting-title">
      <div>
        <h2 id="waiting-title">Waiting for orders from BoostWare…</h2>
        <p>Keep this tab open while BoostWare finishes sending the handoff.</p>
        <LoadingLine label="Checking for your orders…" />
      </div>
    </section>
  );
}

function ExpiredState({ boostwareUrl }: { boostwareUrl: string | null }) {
  return (
    <section className="panel workflow-state-panel" aria-labelledby="expired-title">
      <span className="state-icon warning" aria-hidden="true">
        !
      </span>
      <div>
        <h2 id="expired-title">This handoff has expired</h2>
        <p>Return to BoostWare and start again.</p>
      </div>
      <BoostWareLink href={boostwareUrl} label="Return to BoostWare" />
    </section>
  );
}

function UnauthorizedState({ boostwareUrl }: { boostwareUrl: string | null }) {
  return (
    <section className="panel workflow-state-panel" aria-labelledby="unauthorized-title">
      <span className="state-icon danger" aria-hidden="true">
        ×
      </span>
      <div>
        <h2 id="unauthorized-title">Access denied</h2>
        <p>Open Planning again from BoostWare with an Admin or Ops account.</p>
      </div>
      <BoostWareLink href={boostwareUrl} label="Return to BoostWare" />
    </section>
  );
}

function LoadErrorState({ error, boostwareUrl }: { error: unknown; boostwareUrl: string | null }) {
  return (
    <section className="panel workflow-error-panel" aria-labelledby="load-error-title">
      <Banner tone="error" title="Could not load orders">
        <p id="load-error-title">{errorMessage(error)}</p>
        <ErrorDetails items={errorDetails(error)} />
      </Banner>
      <div className="workflow-state-actions">
        <button className="button button-primary" type="button" onClick={() => location.reload()}>
          Try again
        </button>
        <BoostWareLink href={boostwareUrl} label="Return to BoostWare" />
      </div>
    </section>
  );
}

function BoostWareLink({ href, label }: { href: string | null; label: string }) {
  return href === null ? (
    <button className="button button-secondary" type="button" disabled>
      {label}
    </button>
  ) : (
    <a className="button button-secondary" href={href}>
      {label}
    </a>
  );
}

async function waitForHandoff(handoffId: string, signal: AbortSignal): Promise<HandoffSummary> {
  const deadline = Date.now() + HANDOFF_WAIT_TIMEOUT_MS;
  for (;;) {
    try {
      return await getHandoff(handoffId, signal);
    } catch (reason) {
      if (signal.aborted || isAbortError(reason)) throw reason;
      if (!isInitialRaceError(reason) || Date.now() >= deadline) throw reason;
      await abortableDelay(HANDOFF_POLL_INTERVAL_MS, signal);
    }
  }
}

function abortableDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("The request was aborted.", "AbortError"));
      return;
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);
    function onAbort(): void {
      window.clearTimeout(timer);
      reject(new DOMException("The request was aborted.", "AbortError"));
    }
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function isInitialRaceError(error: unknown): boolean {
  return error instanceof ApiError &&
    (error.code === "PLANNER_SESSION_REQUIRED" || error.code === "HANDOFF_NOT_FOUND")
    ? true
    : error instanceof ApiError && (error.status === 401 || error.status === 404);
}

function classifyFatalState(error: unknown): ScreenState {
  if (error instanceof ApiError && error.status === 410) return { kind: "expired" };
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return { kind: "unauthorized" };
  }
  return { kind: "error", error };
}

function fatalPlanState(error: unknown): ScreenState | null {
  if (error instanceof ApiError && error.status === 410) return { kind: "expired" };
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return { kind: "unauthorized" };
  }
  return null;
}

function handoffFromState(state: ScreenState): HandoffSummary | null {
  return state.kind === "ready" || state.kind === "planning" || state.kind === "success"
    ? state.handoff
    : null;
}

function planMetrics(plan: IntegratedPlanResult): readonly PlanMetric[] {
  const metrics = plan.metrics;
  return [
    { label: "Orders", value: formatNumber(metrics.totalOrders), emphasized: true },
    { label: "Groups", value: formatNumber(metrics.groupCount), emphasized: true },
    { label: "Unique SKU", value: formatNumber(metrics.globalUniqueSkuCount) },
    { label: "Unique locations", value: formatNumber(metrics.globalUniqueLocationCount) },
    { label: "Max orders/group", value: formatNumber(metrics.largestGroupOrderCount) },
    { label: "Max SKU/group", value: formatNumber(metrics.maxGroupUniqueSkuCount) },
    { label: "Repeated SKU", value: formatNumber(metrics.repeatedSkuCount) },
    { label: "Repeated locations", value: formatNumber(metrics.repeatedLocationCount) },
    { label: "Total route cost", value: formatNumber(metrics.totalRouteCost), emphasized: true },
    { label: "Max group route", value: formatNumber(metrics.maxGroupRouteCost) },
    { label: "Max workload", value: formatNumber(metrics.maxGroupWorkload), emphasized: true },
    { label: "Workload imbalance", value: formatNumber(metrics.workloadImbalance) },
  ];
}

function readHandoffId(): string | null {
  const value = new URLSearchParams(window.location.search).get("handoffId")?.trim() ?? "";
  return value.length === 0 ? null : value;
}

function isAbortError(value: unknown): boolean {
  return value instanceof DOMException && value.name === "AbortError";
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-SG");
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-SG", { maximumFractionDigits: 2 }).format(value);
}

function shortId(value: string): string {
  return value.length <= 13 ? value : `${value.slice(0, 8)}…${value.slice(-4)}`;
}
